import React, { useEffect } from "react";
import { Avatar } from "@fluentui/react-components";

/* Small helpers shared by the Teams-style pages (Chat / Calls / Calendar / Activity). */

export type AvatarPx = 16 | 20 | 24 | 28 | 32 | 36 | 40 | 48 | 56 | 64 | 72 | 96;

export const fullName = (c: { firstName?: string | undefined; lastName?: string | undefined }): string =>
  `${c.firstName || ""} ${c.lastName || ""}`.replace(/\s+/g, " ").trim();

export interface PersonAvatarProps {
  name: string;
  image?: string | null | undefined;
  size?: AvatarPx | undefined;
  /** When defined, a Teams-style presence badge is drawn. */
  online?: boolean | undefined;
  className?: string | undefined;
}

export const PersonAvatar: React.FC<PersonAvatarProps> = ({ name, image, size = 32, online, className }) => (
  <Avatar
    className={className}
    name={name}
    size={size}
    color="colorful"
    image={image && image.trim() ? { src: image } : undefined}
    badge={online === undefined ? undefined : { status: online ? "available" : "offline" }}
  />
);

/** Close a popover when the pointer goes down anywhere outside `[data-pop="<id>"]`. */
export const useOutsideClose = (id: string | null, close: () => void): void => {
  useEffect(() => {
    if (!id) return;
    const h = (e: PointerEvent) => {
      const el = e.target as HTMLElement | null;
      if (!el || !el.closest('[data-pop="' + id + '"]')) close();
    };
    document.addEventListener("pointerdown", h);
    return () => document.removeEventListener("pointerdown", h);
  }, [id, close]);
};

/* ---------- formatting ---------- */

export const pad2 = (n: number): string => (n < 10 ? "0" + n : String(n));
export const startOfDay = (ms: number): number => {
  const d = new Date(ms);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
};
export const dayDiff = (ms: number, now: number): number => Math.round((startOfDay(now) - startOfDay(ms)) / 86400000);
export const fmtClock = (ms: number): string => {
  const d = new Date(ms);
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
};
const WEEKDAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const SHORT_DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const SHORT_MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
export const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
export { SHORT_DAYS };

/** "Yesterday", "Thursday", "02/10/2026" – the call-history style. */
export const fmtHistoryDay = (ms: number, now: number): string => {
  const diff = dayDiff(ms, now);
  const d = new Date(ms);
  if (diff <= 0) return "Today";
  if (diff === 1) return "Yesterday";
  if (diff < 7) return WEEKDAYS[d.getDay()] || "";
  return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()}`;
};
/** "10:01", "Yesterday 10:01", "25/09 15:27" – chat message header style. */
export const fmtStamp = (ms: number, now: number): string => {
  const diff = dayDiff(ms, now);
  const d = new Date(ms);
  if (diff <= 0) return fmtClock(ms);
  if (diff === 1) return `Yesterday ${fmtClock(ms)}`;
  return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)} ${fmtClock(ms)}`;
};
/** Date divider in a conversation. */
export const fmtDivider = (ms: number, now: number): string => {
  const diff = dayDiff(ms, now);
  const d = new Date(ms);
  if (diff <= 0) return "Today";
  if (diff === 1) return "Yesterday";
  return `${SHORT_DAYS[d.getDay()]}, ${d.getDate()} ${SHORT_MONTHS[d.getMonth()]}${d.getFullYear() !== new Date(now).getFullYear() ? " " + d.getFullYear() : ""}`;
};
/** "17m 1s" */
export const fmtDur = (s: number): string => {
  if (s <= 0) return "";
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${sec}s`;
  return `${sec}s`;
};
/** "03:07" running timer */
export const fmtTimer = (s: number): string => `${pad2(Math.floor(s / 60))}:${pad2(s % 60)}`;

export const rng = (seed: number): (() => number) => {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
};